import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Loader2, Clock, Check } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 15, 30, 45];

/**
 * Manager-only widget to configure the daily digest email time.
 * Timezone is America/Mexico_City (fixed on the backend).
 */
export default function DigestTimeSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hour, setHour] = useState(8);
  const [minute, setMinute] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (!alive) return;
        setHour(data.digest_hour ?? 8);
        setMinute(data.digest_minute ?? 0);
      } catch (e) {
        toast.error(formatError(e.response?.data?.detail) || "Error cargando ajustes");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const save = async (nextHour, nextMinute) => {
    setSaving(true);
    try {
      await api.patch("/settings", { digest_hour: nextHour, digest_minute: nextMinute });
      setHour(nextHour);
      setMinute(nextMinute);
      toast.success(
        `Digest programado a las ${String(nextHour).padStart(2, "0")}:${String(nextMinute).padStart(2, "0")}`,
      );
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const label = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

  if (loading) {
    return (
      <div className="text-xs text-[#8A796F] flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando...
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="digest-time-settings">
      <div className="flex items-center gap-2 text-sm font-bold text-[#4A3B32]">
        <Clock className="w-4 h-4 text-[#FF7878]" />
        Hora del resumen diario
      </div>
      <p className="text-xs text-[#8A796F]">
        Se envía por email a los Gerentes con notificaciones activas. Actualmente: <b>{label}</b> (México).
      </p>
      <div className="flex items-center gap-2">
        <Select
          value={String(hour)}
          onValueChange={(v) => save(parseInt(v, 10), minute)}
          disabled={saving}
        >
          <SelectTrigger className="h-10 w-24 rounded-full border-[#EADBCE] bg-white" data-testid="digest-hour-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {HOURS.map((h) => (
              <SelectItem key={h} value={String(h)} data-testid={`digest-hour-${h}`}>
                {String(h).padStart(2, "0")} h
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-[#8A796F] font-bold">:</span>
        <Select
          value={String(minute)}
          onValueChange={(v) => save(hour, parseInt(v, 10))}
          disabled={saving}
        >
          <SelectTrigger className="h-10 w-24 rounded-full border-[#EADBCE] bg-white" data-testid="digest-minute-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MINUTES.map((m) => (
              <SelectItem key={m} value={String(m)} data-testid={`digest-minute-${m}`}>
                {String(m).padStart(2, "0")} min
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {saving ? (
          <Loader2 className="w-4 h-4 animate-spin text-[#8A796F]" />
        ) : (
          <Check className="w-4 h-4 text-[#1E7E34]" />
        )}
      </div>
    </div>
  );
}
